import GitHubIcon from "@mui/icons-material/GitHub";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import Alert from "@mui/joy/Alert";
import Avatar from "@mui/joy/Avatar";
import Button from "@mui/joy/Button";
import CircularProgress from "@mui/joy/CircularProgress";
import FormControl from "@mui/joy/FormControl";
import FormHelperText from "@mui/joy/FormHelperText";
import FormLabel from "@mui/joy/FormLabel";
import Grid from "@mui/joy/Grid";
import Input from "@mui/joy/Input";
import Link from "@mui/joy/Link";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import { useEffect, useState } from "react";
import {
  Deployment,
  GitConnection,
  ServiceStack,
  StackStatus,
} from "../../../services/backend_gateway/__generated__/backend_gateway-types";
import GitLabIcon from "../../../components/logos/gitlab-logo-500.svg";
import BitBucketIcon from "../../../components/logos/bitbucket-logo.svg";
import { PageLoading } from "../../../components/page_loading";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import DialogActions from "@mui/joy/DialogActions";
import Switch from "@mui/joy/Switch";
import Typography from "@mui/joy/Typography";

export const DeployCdnServiceComponent = (props: {
  loading: boolean;
  stack: ServiceStack;
  deployment: Deployment | null;
  stackStatus: StackStatus | null;
  gitConnectionArns: GitConnection[];
  queryError: string[];
  removeQueryError: (i: number) => void;
  deployCdnService: (
    gitConnectionArn: string,
    gitRepositoryId: string,
    gitBranch: string,
    buildScriptPath: string,
    buildFolderPath: string,
    buildspecPath: string,
    buildtimeJson: string,
    webHostname: string,
    altFullDomainName: string,
    altCertificateArn: string,
    oidcEnabled: boolean,
    oidcProviderName: string,
    oidcUrl: string,
    oidcClientId: string,
    oidcClientSecret: string,
    oidcThumbprint: string,
    googleEnabled: boolean,
    callbackUri: string,
    logoutUri: string,
    localhostUrl: string,
    wafEnabled: boolean,
    emailPasswordEnabled: boolean
  ) => void;
  refetchStackStatus: () => void;
  removeCdnService: () => void;
  amIRoot: boolean;
  hasFullDomainName: boolean;
  fullDomainName: string | null;
  hasAuthentication: boolean;
  hasGoogleAuthentication: boolean;
}) => {
  const {
    loading,
    stack,
    deployment,
    stackStatus,
    gitConnectionArns,
    queryError,
    removeQueryError,
    deployCdnService,
    refetchStackStatus,
    removeCdnService,
    amIRoot,
    hasFullDomainName,
    fullDomainName,
    hasAuthentication,
    hasGoogleAuthentication,
  } = props;
  const [gitConnectionArn, setGitConnectionArn] = useState<string>("");
  const [gitRepositoryId, setGitRepositoryId] = useState<string>("");
  const [gitBranch, setGitBranch] = useState<string>("");
  const [buildScriptPath, setBuildScriptPath] = useState<string>("");
  const [buildFolderPath, setBuildFolderPath] = useState<string>("");
  const [buildspecPath, setBuildspecPath] = useState<string>("");
  const [buildtimeJson, setBuildtimeJson] = useState<string>("");
  const [webHostname, setWebHostname] = useState<string>("");
  const [altFullDomainName, setAltFullDomainName] = useState<string>("");
  const [altCertificateArn, setAltCertificateArn] = useState<string>("");
  const [oidcEnabled, setOidcEnabled] = useState<boolean>(false);
  const [oidcProviderName, setOidcProviderName] = useState<string>("");
  const [oidcUrl, setOidcUrl] = useState<string>("");
  const [oidcClientId, setOidcClientId] = useState<string>("");
  const [oidcClientSecret, setOidcClientSecret] = useState<string>("");
  const [oidcThumbprint, setOidcThumbprint] = useState<string>("");
  const [googleEnabled, setGoogleEnabled] = useState<boolean>(false);
  const [callbackUri, setCallbackUri] = useState<string>("");
  const [logoutUri, setLogoutUri] = useState<string>("");
  const [localhostUrl, setLocalhostUrl] = useState<string>("");
  const [wafEnabled, setWafEnabled] = useState<boolean>(false);
  const [emailPasswordEnabled, setEmailPasswordEnabled] =
    useState<boolean>(false);

  const [openModal, setOpenModal] = useState<boolean>(false);

  useEffect(() => {
    if (stackStatus !== null && (stackStatus.parametersJSON || null) !== null) {
      try {
        const params = JSON.parse(stackStatus.parametersJSON!);
        setGitConnectionArn(params.GitConnectionArn || "");
        setGitRepositoryId(params.GitRepositoryId || "");
        setGitBranch(params.GitBranch || "");
        setBuildScriptPath(params.BuildScriptPath || "");
        setBuildFolderPath(params.BuildFolderPath || "");
        setBuildspecPath(params.BuildspecPath || "");
        setBuildtimeJson(params.BuildEnvVarsJson || "");
        setWebHostname(params.WebHostname || "");
        setAltFullDomainName(params.AltFullDomainName || "");
        setAltCertificateArn(params.AltCertificateArn || "");
        setOidcEnabled(params.OidcEnabled === "True");
        setOidcProviderName(params.OidcProviderName || "");
        setOidcUrl(params.OidcUrl || "");
        setOidcClientId(params.OidcClientId || "");
        setOidcClientSecret(params.OidcClientSecret || "");
        setOidcThumbprint(params.OidcThumbprint || "");
        setGoogleEnabled(params.GoogleEnabled === "True");
        setCallbackUri(params.CallbackUri || "");
        setLogoutUri(params.LogoutUri || "");
        setLocalhostUrl(params.LocalhostUrl || "");
        setWafEnabled(params.WafEnabled === "True");
        setEmailPasswordEnabled(params.EmailPasswordEnabled === "True");
      } catch (err) {
        console.info("Failed to parse parametersJSON");
      }
    }
  }, [stackStatus]);

  return (
    <>
      <PageLoading loading={loading} />
      {queryError.map((e, i) => (
        <Alert
          key={i}
          color="danger"
          variant="soft"
          endDecorator={
            <Button
              color="danger"
              variant="soft"
              onClick={() => removeQueryError(i)}
            >
              DISMISS
            </Button>
          }
          sx={{ mb: 2 }}
        >
          Error: {e}
        </Alert>
      ))}
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        {!loading && (stackStatus?.status || null) === null && (
          <Grid xs={12}>
            <Alert color="neutral" variant="soft" sx={{ mb: 2 }}>
              The service has not been deployed to this environment yet.
            </Alert>
          </Grid>
        )}
        {!loading &&
          stackStatus?.status !== null &&
          stackStatus?.status !== undefined && (
            <Grid xs={12}>
              <Alert
                color={
                  stackStatus?.status === "CREATE_COMPLETE" ||
                  stackStatus?.status === "UPDATE_COMPLETE"
                    ? "success"
                    : (stackStatus?.status || "").indexOf("CREATE") > -1 ||
                      (stackStatus?.status || "").indexOf("UPDATE") > -1
                    ? "primary"
                    : (stackStatus?.status || "").indexOf("ROLLBACK") > -1
                    ? "danger"
                    : "neutral"
                }
                variant="soft"
                sx={{ mb: 2 }}
                startDecorator={
                  (stackStatus?.status || "").indexOf("IN_PROGRESS") > -1 ? (
                    <CircularProgress
                      size="sm"
                      color={
                        stackStatus?.status === "CREATE_COMPLETE" ||
                        stackStatus?.status === "UPDATE_COMPLETE"
                          ? "success"
                          : (stackStatus?.status || "").indexOf("CREATE") >
                              -1 ||
                            (stackStatus?.status || "").indexOf("UPDATE") > -1
                          ? "primary"
                          : (stackStatus?.status || "").indexOf("ROLLBACK") > -1
                          ? "danger"
                          : "neutral"
                      }
                    />
                  ) : null
                }
                endDecorator={
                  (stackStatus?.status || "").indexOf("IN_PROGRESS") === -1 ? (
                    <Button
                      size="sm"
                      variant="soft"
                      color={
                        stackStatus?.status === "CREATE_COMPLETE" ||
                        stackStatus?.status === "UPDATE_COMPLETE"
                          ? "success"
                          : (stackStatus?.status || "").indexOf("CREATE") >
                              -1 ||
                            (stackStatus?.status || "").indexOf("UPDATE") > -1
                          ? "primary"
                          : (stackStatus?.status || "").indexOf("ROLLBACK") > -1
                          ? "danger"
                          : "neutral"
                      }
                      onClick={() => {
                        refetchStackStatus();
                      }}
                    >
                      Refresh
                    </Button>
                  ) : null
                }
              >
                Deployment status: {stackStatus?.status}
                <Link
                  href={`https://${deployment?.awsRegion}.console.aws.amazon.com/cloudformation/home?region=${deployment?.awsRegion}#/stacks/stackinfo?filteringText=&filteringStatus=active&viewNested=true&stackId=waffle-${deployment?.id}-${stack?.id}-cdn-cicd`}
                >
                  Details
                </Link>
              </Alert>
            </Grid>
          )}
        <Grid xs={12} sm={6}>
          <FormControl>
            <FormLabel>Select your git connection</FormLabel>
            <Select
              placeholder="Select Git Connection"
              value={gitConnectionArn}
              slotProps={{
                listbox: {
                  sx: {
                    maxHeight: 300,
                    overflow: "auto", // required for scrolling
                    zIndex: 1201, // Because of the floating drawer on mobile
                  },
                },
              }}
            >
              {gitConnectionArns?.map((conn, i) => (
                <Option
                  value={conn.id}
                  key={conn.id || i}
                  onClick={() => {
                    setGitConnectionArn(conn.id || "");
                  }}
                >
                  <ListItemDecorator>
                    {conn.providerType === "GitHub" ||
                    conn.providerType === "GitHubEnterpriseServer" ? (
                      <GitHubIcon />
                    ) : conn.providerType === "Bitbucket" ? (
                      <Avatar src={BitBucketIcon} />
                    ) : (
                      <Avatar src={GitLabIcon} />
                    )}
                  </ListItemDecorator>
                  {conn.name}
                </Option>
              ))}
            </Select>
            <FormHelperText>
              Select the connection to your git provider that has the repository
              that you want to deploy
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl>
            <FormLabel>Git Repository</FormLabel>
            <Input
              value={gitRepositoryId}
              onChange={(event) => {
                setGitRepositoryId(event.target.value);
              }}
            />
            <FormHelperText>
              The owner and name of the repo expected in the following format:
              some-user/my-repo
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormLabel>Git Branch</FormLabel>
          <Input
            value={gitBranch}
            onChange={(event) => {
              setGitBranch(event.target.value);
            }}
          />
          <FormHelperText>The branch in the repo to deploy</FormHelperText>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl>
            <FormLabel>Build command</FormLabel>
            <Input
              value={buildScriptPath}
              onChange={(event) => {
                setBuildScriptPath(event.target.value);
              }}
            />
            <FormHelperText>
              Set the the build command that will be used to build the static
              frontend files from the source code. Has to include the path in
              the repo.
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl>
            <FormLabel>Build folder path</FormLabel>
            <Input
              value={buildFolderPath}
              onChange={(event) => {
                setBuildFolderPath(event.target.value);
              }}
            />
            <FormHelperText>
              Set the path to the folder where the build script produces the
              static frontend files.
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl>
            <FormLabel>Buildpsec path (Optional)</FormLabel>
            <Input
              value={buildspecPath}
              onChange={(event) => {
                setBuildspecPath(event.target.value);
              }}
            />
            <FormHelperText>
              Only processed if the build script path is unset. Set the path in
              the repo to the buildspec.yml file with custom build instructions.
              The path has to include the filename as well.
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl>
            <FormLabel>Build-time JSON</FormLabel>
            <Input
              value={buildtimeJson}
              onChange={(event) => {
                setBuildtimeJson(event.target.value);
              }}
            />
            <FormHelperText>
              An optional JSON string that you can send access when building the
              container as an environmental variable. Unsure? Leave it empty.
            </FormHelperText>
          </FormControl>
        </Grid>

        {hasFullDomainName && (
          <Grid xs={12} sm={6}>
            <FormControl>
              <FormLabel>Website Hostname</FormLabel>
              <Input
                value={webHostname}
                // placeholder="www"
                onChange={(event) => {
                  setWebHostname(event.target.value);
                }}
              />
              <FormHelperText>
                If you'd like to deploy this frontend to www.
                {fullDomainName || "dev.example.com"}, then this value would be
                "www". Additionally you can deploy this frontend with a custom
                domain name too, see the next fields below.
              </FormHelperText>
            </FormControl>
          </Grid>
        )}

        <Grid xs={12} sm={6}>
          <FormControl>
            <FormLabel>Alternative Domain Name</FormLabel>
            <Input
              value={altFullDomainName}
              onChange={(event) => {
                setAltFullDomainName(event.target.value);
              }}
            />
            <FormHelperText>
              If you'd like to deploy this frontend to a different hostname,
              like mycompletely.differentdomain.com then you can specify that
              here. Heads up: it only works if you can create an SSL certificate
              for that in this AWS account. Waffle doesn't automatically do
              that.
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl>
            <FormLabel>Alternative Domain SSL Certificate ARN</FormLabel>
            <Input
              value={altCertificateArn}
              onChange={(event) => {
                setAltCertificateArn(event.target.value);
              }}
            />
            <FormHelperText>
              If you have specified an alternative domain name then you need to
              create a certificate for that using the AWS certificate manager in
              this ({deployment?.name || deployment?.id || ""}) account, and
              enter its ARN here.
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={12}>
          <Typography level="title-lg">Authentication</Typography>
          <Typography level="body-sm">
            The following settings help set up authorization using two
            alternative mechanisms. One is to use an Open ID Connect (OIDC)
            identity provider to assume AWS IAM roles that enable the user to
            access the API Gateway. The other is to use Cognito User Pools to
            manage users and authorize users at the API Gateway either with JWT
            tokens or by AWS IAM roles. The Cognito User Pool has to be first
            configured in the environment.
          </Typography>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl>
            <FormLabel>OIDC Enabled</FormLabel>
            <Typography
              component="label"
              startDecorator={
                <Switch
                  checked={oidcEnabled}
                  onChange={(event) => {
                    setOidcEnabled(event.target.checked);
                  }}
                />
              }
            >
              {oidcEnabled ? "Enabled" : "Disabled"}
            </Typography>
            <FormHelperText>
              If enabled, the service will use OIDC for authentication. The OIDC
              will act as an identity provider for IAM authentication, but
              doesn't integrate with Cognito User Pools.
            </FormHelperText>
          </FormControl>
        </Grid>

        {oidcEnabled && (
          <>
            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>OIDC Provider Name</FormLabel>
                <Input
                  value={oidcProviderName}
                  onChange={(event) => {
                    setOidcProviderName(event.target.value);
                  }}
                />
                <FormHelperText>The name of the OIDC provider.</FormHelperText>
              </FormControl>
            </Grid>

            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>OIDC URL</FormLabel>
                <Input
                  value={oidcUrl}
                  onChange={(event) => {
                    setOidcUrl(event.target.value);
                  }}
                />
                <FormHelperText>The URL of the OIDC provider.</FormHelperText>
              </FormControl>
            </Grid>

            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>OIDC Client ID</FormLabel>
                <Input
                  value={oidcClientId}
                  onChange={(event) => {
                    setOidcClientId(event.target.value);
                  }}
                />
                <FormHelperText>
                  The client ID of the OIDC provider.
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>OIDC Client Secret</FormLabel>
                <Input
                  type="password"
                  value={oidcClientSecret}
                  onChange={(event) => {
                    setOidcClientSecret(event.target.value);
                  }}
                />
                <FormHelperText>
                  The client secret of the OIDC provider.
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>OIDC Thumbprint</FormLabel>
                <Input
                  value={oidcThumbprint}
                  onChange={(event) => {
                    setOidcThumbprint(event.target.value);
                  }}
                />
                <FormHelperText>
                  (Optional) The thumbprint of the OIDC provider. Please refer
                  to the AWS documentation for more information.
                </FormHelperText>
              </FormControl>
            </Grid>
          </>
        )}

        {hasAuthentication && hasGoogleAuthentication && (
          <Grid xs={12} sm={6}>
            <FormControl>
              <FormLabel>Google Enabled</FormLabel>
              <Typography
                component="label"
                startDecorator={
                  <Switch
                    checked={googleEnabled}
                    onChange={(event) => {
                      setGoogleEnabled(event.target.checked);
                    }}
                  />
                }
              >
                {googleEnabled ? "Enabled" : "Disabled"}
              </Typography>
              <FormHelperText>
                If enabled, then if the Cognito User Pool does have a Google
                integration, then the service will be able to use it for
                authentication.
              </FormHelperText>
            </FormControl>
          </Grid>
        )}

        {hasAuthentication && (
          <>
            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>Callback URL path</FormLabel>
                <Input
                  value={callbackUri}
                  onChange={(event) => {
                    setCallbackUri(event.target.value);
                  }}
                />
                <FormHelperText>
                  The callback URL path for the Cognito User Pool Client.
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>Logout URL path</FormLabel>
                <Input
                  value={logoutUri}
                  onChange={(event) => {
                    setLogoutUri(event.target.value);
                  }}
                />
                <FormHelperText>
                  The logout URL path for the Cognito User Pool Client.
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>Localhost URL</FormLabel>
                <Input
                  value={localhostUrl}
                  onChange={(event) => {
                    setLocalhostUrl(event.target.value);
                  }}
                />
                <FormHelperText>
                  The localhost URL for the Cognito User Pool Client for local
                  devlopment.
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>Email Password Enabled</FormLabel>
                <Typography
                  component="label"
                  startDecorator={
                    <Switch
                      checked={emailPasswordEnabled}
                      onChange={(event) => {
                        setEmailPasswordEnabled(event.target.checked);
                      }}
                    />
                  }
                >
                  {emailPasswordEnabled ? "Enabled" : "Disabled"}
                </Typography>
                <FormHelperText>
                  If enabled, users will be able to use email and password to
                  login.
                </FormHelperText>
              </FormControl>
            </Grid>
          </>
        )}
        <Grid xs={12} sm={6}>
          <FormControl>
            <FormLabel>WAF Enabled</FormLabel>
            <Typography
              component="label"
              startDecorator={
                <Switch
                  checked={wafEnabled}
                  onChange={(event) => {
                    setWafEnabled(event.target.checked);
                  }}
                />
              }
            >
              {wafEnabled ? "Enabled" : "Disabled"}
            </Typography>
            <FormHelperText>
              If enabled, the service will use afirewall to protect the service.
              (AWS WAF to CloudFront)
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid xs={12}>
          <Button
            onClick={() =>
              deployCdnService(
                gitConnectionArn,
                gitRepositoryId,
                gitBranch,
                buildScriptPath,
                buildFolderPath,
                buildspecPath,
                buildtimeJson,
                webHostname,
                altFullDomainName,
                altCertificateArn,
                oidcEnabled,
                oidcProviderName,
                oidcUrl,
                oidcClientId,
                oidcClientSecret,
                oidcThumbprint,
                googleEnabled,
                callbackUri,
                logoutUri,
                localhostUrl,
                wafEnabled,
                emailPasswordEnabled
              )
            }
            disabled={
              loading || (stackStatus?.status || "").indexOf("IN_PROGRESS") > -1
            }
          >
            {(stackStatus?.status || null) === null ? "Deploy" : "Update"}{" "}
            Service
          </Button>
          {(stackStatus?.status || null) !== null && amIRoot && (
            <Button
              onClick={() => setOpenModal(true)}
              disabled={
                loading ||
                (stackStatus?.status || "").indexOf("IN_PROGRESS") > -1 ||
                (stackStatus?.status || "").indexOf("DELETE_FAILED") > -1
              }
              variant="plain"
              color="neutral"
            >
              Remove Service
            </Button>
          )}
        </Grid>
      </Grid>
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>
            <WarningRoundedIcon />
            Are you sure to remove the service?
          </DialogTitle>
          <DialogContent>
            This action will remove the service and all associated resources
            including the CICD pipeline.
          </DialogContent>
          <DialogActions>
            <Button
              variant="solid"
              color="danger"
              onClick={() => {
                removeCdnService();
                setOpenModal(false);
              }}
            >
              Remove Service
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setOpenModal(false)}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};
